import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Users } from "lucide-react";
import { format } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/dashboard/status-badge";
import { DecisionBadge } from "@/components/interviews/decision";
import { FilterBar } from "@/components/interviews/filter-bar";
import type { InterviewStatus } from "@/lib/constants";
import { initials } from "@/lib/utils";
import { useDict, useLocale } from "@/lib/i18n/context";

type CandidateRow = {
  id: string;
  name: string;
  email: string;
  job_title: string | null;
  interview_score: number | null;
  interview_status: string | null;
  interview_decision: string | null;
  interview_id: string | null;
  created_at: string;
};

export function CandidatesPage() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const [searchParams] = useSearchParams();
  const [candidates, setCandidates] = useState<CandidateRow[] | null>(null);
  const [jobs, setJobs] = useState<{ id: string; title: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const q = searchParams.get("q") || "";
  const status = searchParams.get("status") || "";
  const decision = searchParams.get("decision") || "";
  const job = searchParams.get("job") || "";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const hasFilters = Boolean(q || status || decision || job || from || to);
  const qs = searchParams.toString();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetch(`/api/candidates${qs ? `?${qs}` : ""}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<{
          candidates: CandidateRow[];
          jobs: { id: string; title: string }[];
        }>;
      })
      .then((data) => {
        if (active) {
          setCandidates(data.candidates);
          setJobs(data.jobs);
        }
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [qs]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (error || !candidates) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t.candidates.title}</h1>
          <p className="text-muted">{t.candidates.subtitle}</p>
        </div>
        <Link to="/candidates/new">
          <Button>
            <Plus className="h-4 w-4" /> {t.candidates.inviteCandidate}
          </Button>
        </Link>
      </div>

      <FilterBar
        basePath="/candidates"
        jobs={jobs}
        initial={{ q, status, decision, job, from, to }}
      />

      {candidates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <Users className="h-10 w-10 text-muted/60" />
            <p className="mt-4 font-medium">
              {hasFilters ? t.candidates.noMatches : t.candidates.empty}
            </p>
            {!hasFilters && (
              <Link to="/candidates/new">
                <Button className="mt-4">
                  <Plus className="h-4 w-4" /> {t.candidates.inviteCandidate}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-start text-xs font-medium uppercase tracking-wide text-muted">
                  <th className="px-4 py-3 text-start">{t.candidates.colCandidate}</th>
                  <th className="px-4 py-3 text-start">{t.candidates.colJob}</th>
                  <th className="px-4 py-3 text-start">{t.candidates.colScore}</th>
                  <th className="px-4 py-3 text-start">{t.candidates.colStatus}</th>
                  <th className="px-4 py-3 text-start">{t.candidates.colDecision}</th>
                  <th className="hidden px-4 py-3 text-start md:table-cell">
                    {t.candidates.colInvited}
                  </th>
                  <th className="px-4 py-3 text-end">{t.candidates.colActions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {candidates.map((c) => (
                  <tr key={c.id} className="hover:bg-surface">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                          {initials(c.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-medium">{c.name}</p>
                          <p className="truncate text-xs text-muted">{c.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted">
                      {c.job_title ?? t.common.generalInterview}
                    </td>
                    <td className="px-4 py-3">
                      {c.interview_score != null ? (
                        <span className="font-semibold">{c.interview_score}/100</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.interview_status ? (
                        <StatusBadge status={c.interview_status as InterviewStatus} />
                      ) : (
                        <span className="text-xs text-muted">
                          {t.candidates.noInterview}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {c.interview_decision ? (
                        <DecisionBadge decision={c.interview_decision} />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="hidden px-4 py-3 text-xs text-muted md:table-cell">
                      {format(new Date(c.created_at), "PP", { locale: dateLocale })}
                    </td>
                    <td className="px-4 py-3 text-end">
                      {c.interview_id ? (
                        <Link
                          to={`/interviews/${c.interview_id}`}
                          className="font-medium text-brand-600 hover:underline"
                        >
                          {t.candidates.view}
                        </Link>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
